import { UPLOAD_SCENE } from '@/app/constants';
import type { FormImagePrompt, ImagePrompt, ImageRange, ReferencePrompt } from '@/app/types';
import { extractTempImageData, type TempImageUploadOutcome, uploadFile, uploadTempImages } from '@/app/utils/client';
import type { FormPromptListHelpers, TempUploadItem, TempUploadRefs, TempUploadReplacement } from '../types';
import { toolTempUploadKey } from './temp-upload-key';

function isKeyScheduled(refs: TempUploadRefs, key: string): boolean {
  return refs.processed.current.has(key) || refs.pending.current.has(key);
}

function getImagePromptReplacements(before: ImagePrompt, after: ImagePrompt): TempUploadReplacement[] {
  return [
    { from: before.sourceImage, to: after.sourceImage },
    { from: before.selectedImage, to: after.selectedImage },
  ].filter(({ from, to }) => !!from && !!to && from !== to);
}

/**
 * 补传失败时抛出，交给 useRunTempUploadBatch：
 * 抛出后 uploadKey 不会进 processed，下一轮 effect 会自动重试。
 */
function throwUploadFailure(outcome: Extract<TempImageUploadOutcome, { ok: false }>): never {
  throw new Error(`Temp image upload failed at ${outcome.stage}: ${String(outcome.error?.reason ?? outcome.error)}`);
}

function scaleImageRange(
  range: ImageRange | undefined,
  from: { width?: number; height?: number },
  to: { width: number; height: number },
): ImageRange | undefined {
  if (!range || !from.width || !from.height || !to.width || !to.height) return range;

  const scaleX = to.width / from.width;
  const scaleY = to.height / from.height;

  return [
    Math.round(range[0] * scaleX),
    Math.round(range[1] * scaleY),
    Math.round(range[2] * scaleX),
    Math.round(range[3] * scaleY),
  ];
}

/**
 * 用补传结果拼出已上传的 ImagePrompt 字段。
 *
 * 没有独立 selected 图时回落到 source 是正常的（整图即选中区域）；
 * selected 图上传失败不会走到这里 —— 那种情况在 uploadTempImages 里就返回 ok:false 了，
 * 绝不能静默拿整图替代裁剪图去生成。
 */
export function buildUploadedImageFields(
  currentPrompt: ImagePrompt,
  result: Extract<TempImageUploadOutcome, { ok: true }>,
): ImagePrompt {
  const selectedWidth = currentPrompt._tempSelectedImage
    ? (currentPrompt.selectedImageWidth ?? currentPrompt.sourceImageWidth ?? 0)
    : (currentPrompt._tempSourceFallback?.width ??
      currentPrompt.selectedImageWidth ??
      currentPrompt.sourceImageWidth ??
      0);
  const selectedHeight = currentPrompt._tempSelectedImage
    ? (currentPrompt.selectedImageHeight ?? currentPrompt.sourceImageHeight ?? 0)
    : (currentPrompt._tempSourceFallback?.height ??
      currentPrompt.selectedImageHeight ??
      currentPrompt.sourceImageHeight ??
      0);
  const { _tempFile, _tempSelectedImage, _tempSourceFallback, _tempUploadError, ...promptWithoutTemp } = currentPrompt;
  const sourceImageWidth = result.sourceFromSelected
    ? selectedWidth
    : result.sourceDegraded && _tempSourceFallback
      ? _tempSourceFallback.width
      : (currentPrompt.sourceImageWidth ?? 0);
  const sourceImageHeight = result.sourceFromSelected
    ? selectedHeight
    : result.sourceDegraded && _tempSourceFallback
      ? _tempSourceFallback.height
      : (currentPrompt.sourceImageHeight ?? 0);
  const selectedImageWidth = result.selectedUri ? selectedWidth : sourceImageWidth;
  const selectedImageHeight = result.selectedUri ? selectedHeight : sourceImageHeight;
  const selectedImageRange = result.sourceFromSelected
    ? ([0, 0, sourceImageWidth, sourceImageHeight] as ImageRange)
    : result.sourceDegraded
      ? scaleImageRange(
          promptWithoutTemp.selectedImageRange,
          { width: currentPrompt.sourceImageWidth, height: currentPrompt.sourceImageHeight },
          { width: sourceImageWidth, height: sourceImageHeight },
        )
      : promptWithoutTemp.selectedImageRange;
  const sourceImagePreview = result.sourceFromSelected
    ? currentPrompt.selectedImagePreview || currentPrompt.sourceImagePreview
    : result.sourceDegraded && _tempSourceFallback
      ? URL.createObjectURL(_tempSourceFallback.blob)
      : currentPrompt.sourceImagePreview;
  return {
    ...promptWithoutTemp,
    sourceImage: result.sourceUri,
    sourceImagePreview,
    sourceImageWidth,
    sourceImageHeight,
    selectedImage: result.selectedUri ?? result.sourceUri,
    selectedImagePreview: result.selectedUri
      ? currentPrompt.selectedImagePreview || currentPrompt.sourceImagePreview
      : result.sourceDegraded
        ? result.sourceUri
        : currentPrompt.sourceImagePreview,
    selectedImageWidth,
    selectedImageHeight,
    selectedImageRange,
  };
}

/**
 * 列表型图片补传（img2video / reference / 各 tool 表单共用）。
 *
 * 两种情况：
 * - tempData 存在（有 _tempFile）：未登录时选的图，原图 + 裁剪图都要补传
 * - 只有 _tempSelectedImage：源图本就是远端图（如示例图），只补传裁剪图
 */
export function getTempImagesPendingItems(
  refs: TempUploadRefs,
  helpers: FormPromptListHelpers<FormImagePrompt>,
  ctx: { imagePrompts: Array<FormImagePrompt | null> },
  keyPrefix = 'image',
): TempUploadItem[] {
  const { updatePromptItem, markPromptStatus } = helpers;
  const pendingUploads = ctx.imagePrompts
    .map((prompt) => {
      if (!prompt?.prompt) return null;
      const tempData = extractTempImageData(prompt.prompt);
      const hasTempSelectedImage = !!prompt.prompt._tempSelectedImage;
      if (!tempData && !hasTempSelectedImage) return null;
      const uploadKey = tempData
        ? toolTempUploadKey(keyPrefix, prompt.id, tempData.id)
        : toolTempUploadKey(`${keyPrefix}-selected`, prompt.id);
      if (isKeyScheduled(refs, uploadKey)) return null;
      return { prompt, tempData, uploadKey };
    })
    .filter(
      (
        item,
      ): item is {
        prompt: FormImagePrompt;
        tempData: NonNullable<ReturnType<typeof extractTempImageData>> | null;
        uploadKey: string;
      } => item !== null,
    );

  return pendingUploads.map(({ prompt, tempData, uploadKey }) => ({
    uploadKey,
    run: async (): Promise<TempUploadReplacement[]> => {
      markPromptStatus(prompt.id, 'uploading');
      if (tempData) {
        const results = await uploadTempImages([tempData]);
        const result = results.get(tempData.id);
        const promptData = prompt.prompt;
        if (!result || !promptData) {
          markPromptStatus(prompt.id, 'done');
          throw new Error('Upload temp image failed');
        }
        if (!result.ok) {
          markPromptStatus(prompt.id, 'done');
          updatePromptItem(prompt.id, (latestPrompt) => ({
            ...latestPrompt,
            prompt: latestPrompt.prompt ? { ...latestPrompt.prompt, _tempUploadError: true } : latestPrompt.prompt,
          }));
          throwUploadFailure(result);
        }
        const uploadedPrompt = buildUploadedImageFields(promptData, result);
        updatePromptItem(prompt.id, (latestPrompt) => ({
          ...latestPrompt,
          status: 'done',
          prompt: uploadedPrompt,
        }));
        return getImagePromptReplacements(promptData, uploadedPrompt);
      }
      const currentPrompt = prompt.prompt;
      if (!currentPrompt?._tempSelectedImage) {
        markPromptStatus(prompt.id, 'done');
        return [];
      }
      const selectedUploadResult = await uploadFile({
        file: currentPrompt._tempSelectedImage,
        width: currentPrompt.selectedImageWidth ?? currentPrompt.sourceImageWidth ?? 0,
        height: currentPrompt.selectedImageHeight ?? currentPrompt.sourceImageHeight ?? 0,
        scene: UPLOAD_SCENE.VIDU,
      });
      if (selectedUploadResult.error || !selectedUploadResult.uri) {
        console.error('Failed to upload temp selected image:', prompt.id, selectedUploadResult.error);
        markPromptStatus(prompt.id, 'done');
        updatePromptItem(prompt.id, (latestPrompt) => ({
          ...latestPrompt,
          prompt: latestPrompt.prompt ? { ...latestPrompt.prompt, _tempUploadError: true } : latestPrompt.prompt,
        }));
        throw new Error(String(selectedUploadResult.error));
      }
      const { _tempSelectedImage, _tempUploadError, ...rest } = currentPrompt;
      const uploadedPrompt: ImagePrompt = {
        ...rest,
        selectedId: selectedUploadResult.id ?? rest.selectedId,
        selectedImage: selectedUploadResult.uri,
        selectedImagePreview: rest.selectedImagePreview || rest.sourceImagePreview,
      };
      updatePromptItem(prompt.id, (latestPrompt) => {
        if (!latestPrompt.prompt) return latestPrompt;
        return {
          ...latestPrompt,
          status: 'done',
          prompt: uploadedPrompt,
        };
      });
      return getImagePromptReplacements(currentPrompt, uploadedPrompt);
    },
  }));
}

export function getReferenceTempImagesPendingItems(
  refs: TempUploadRefs,
  referencePrompts: ReferencePrompt[],
  updateReferenceAndMentions: (id: string, updates: Partial<ReferencePrompt>) => void,
): TempUploadItem[] {
  const imagePrompts = referencePrompts.filter(
    (item): item is Extract<ReferencePrompt, { type: 'image' }> => item.type === 'image',
  );
  return getTempImagesPendingItems(
    refs,
    {
      updatePromptItem: (id, updater) => {
        const target = imagePrompts.find((item) => item.id === id);
        if (!target?.prompt) return;
        const next = updater(target);
        updateReferenceAndMentions(id, { prompt: next.prompt, status: next.status });
      },
      markPromptStatus: (id, status) => updateReferenceAndMentions(id, { status }),
    },
    { imagePrompts },
  );
}

/** 单张源图补传（quick-edit 等单图工具用） */
export function getTempSourceImageItems(
  refs: TempUploadRefs,
  ctx: {
    imagePrompt: ImagePrompt | null;
    getImagePrompt: () => ImagePrompt | null;
    setImagePrompt: (imagePrompt: ImagePrompt | null) => void;
    onStatusChange?: (status: 'uploading' | 'done') => void;
  },
): TempUploadItem[] {
  const { imagePrompt, getImagePrompt, setImagePrompt, onStatusChange } = ctx;
  if (!imagePrompt) return [];
  const tempData = extractTempImageData(imagePrompt);
  const hasTempSelectedImage = !!imagePrompt._tempSelectedImage;
  if (!tempData && !hasTempSelectedImage) return [];
  const uploadKey = tempData
    ? toolTempUploadKey('source-image', tempData.id)
    : toolTempUploadKey('source-image-selected', String(imagePrompt.selectedId || imagePrompt.sourceImage));
  if (isKeyScheduled(refs, uploadKey)) return [];

  return [
    {
      uploadKey,
      run: async (): Promise<TempUploadReplacement[]> => {
        onStatusChange?.('uploading');
        try {
          const currentImagePrompt = getImagePrompt();
          if (!currentImagePrompt) {
            return [];
          }
          if (tempData) {
            const results = await uploadTempImages([tempData]);
            const result = results.get(tempData.id);
            if (!result) {
              throw new Error('Upload temp source image failed');
            }
            if (!result.ok) {
              const latestImagePrompt = getImagePrompt();
              if (latestImagePrompt) {
                setImagePrompt({ ...latestImagePrompt, _tempUploadError: true });
              }
              throwUploadFailure(result);
            }
            const uploadedPrompt = buildUploadedImageFields(currentImagePrompt, result);
            setImagePrompt(uploadedPrompt);
            return getImagePromptReplacements(currentImagePrompt, uploadedPrompt);
          }
          if (!currentImagePrompt._tempSelectedImage) {
            return [];
          }
          const selectedUploadResult = await uploadFile({
            file: currentImagePrompt._tempSelectedImage,
            width: currentImagePrompt.selectedImageWidth ?? currentImagePrompt.sourceImageWidth ?? 0,
            height: currentImagePrompt.selectedImageHeight ?? currentImagePrompt.sourceImageHeight ?? 0,
            scene: UPLOAD_SCENE.VIDU,
          });
          if (selectedUploadResult.error || !selectedUploadResult.uri) {
            console.error('Failed to upload temp selected source image:', selectedUploadResult.error);
            const latestImagePrompt = getImagePrompt();
            if (latestImagePrompt) {
              setImagePrompt({ ...latestImagePrompt, _tempUploadError: true });
            }
            throw new Error(String(selectedUploadResult.error));
          }
          const latestImagePrompt = getImagePrompt();
          if (!latestImagePrompt) {
            return [];
          }
          const { _tempSelectedImage, _tempUploadError, ...rest } = latestImagePrompt;
          const uploadedPrompt: ImagePrompt = {
            ...rest,
            selectedId: selectedUploadResult.id ?? rest.selectedId,
            selectedImage: selectedUploadResult.uri,
            selectedImagePreview: rest.selectedImagePreview || rest.sourceImagePreview,
          };
          setImagePrompt(uploadedPrompt);
          return getImagePromptReplacements(latestImagePrompt, uploadedPrompt);
        } finally {
          onStatusChange?.('done');
        }
      },
    },
  ];
}
